import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';

import { Subscription } from 'rxjs';

import { FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';

export interface TimepickerProps {
  timepickerOptions?: Partial<{
    label: string;
  }>;
}

export interface DateTimeProps extends FormlyFieldProps, TimepickerProps {
  // default is date-time
  presentation?: 'date' | 'date-time';
  showDefaultButton?: boolean;
  hourValues?: number;
  minuteValues?: number;

  // default value if no explicit default value is given
  // now-date-time will set both date and time to current time
  // now-date-start-of-day will set date to today and time to 00:00 in user timezone
  // now-date-end-of-day will set date to today and time to 23:59 in user timezone
  defaultDateTime?:
    | 'now-date-time'
    | 'now-date-start-of-day'
    | 'now-date-end-of-day';
}

@Component({
  selector: 'ts-date-time-form-field-ui',
  templateUrl: './date-time-form-field-ui.component.html',
  styleUrls: ['./date-time-form-field-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatTimepickerModule,
  ],
})
export class DateTimeFormFieldUiComponent
  extends FieldType<FieldTypeConfig<DateTimeProps>>
  implements OnInit, OnDestroy
{
  datetimeFormControl = new FormControl<Date | undefined>(undefined);
  subscription?: Subscription;

  ngOnInit(): void {
    let initialDate: Date | null;
    let initialFormControlValue: number | null = null; // in microseconds
    let initialDatepickerFormValue: Date | null = null;

    if (this.formControl.value) {
      if (!isNaN(+this.formControl.value)) {
        initialDate = new Date(+this.formControl.value / 1000);
      } else {
        initialDate = new Date(this.formControl.value);
      }

      initialFormControlValue = !isNaN(initialDate.getTime())
        ? initialDate.getTime() * 1000
        : null;
      initialDatepickerFormValue = !isNaN(initialDate.getTime())
        ? initialDate
        : null;
    }

    if (!this.formControl.value && this.props.defaultDateTime) {
      initialDate = this.getDefaultDate(this.props.defaultDateTime!);
      initialFormControlValue = initialDate.getTime() * 1000;
      initialDatepickerFormValue = initialDate;
    }

    this.formControl.setValue(initialFormControlValue, { emitEvent: false });
    this.datetimeFormControl.setValue(initialDatepickerFormValue, {
      emitEvent: false,
    });

    // Subscribe to datetimeFormControl changes and update formControl
    const datetimeToFormSubscription =
      this.datetimeFormControl.valueChanges.subscribe(
        (date: Date | null | undefined) => {
          if (!date) {
            this.formControl.setValue(null);
          } else {
            const formValue = new Date(date);

            if (this.props.hourValues) {
              formValue.setHours(this.props.hourValues!);
            }

            if (this.props.minuteValues) {
              formValue.setMinutes(this.props.minuteValues!);
            }

            this.formControl.setValue(formValue.getTime() * 1000);
          }
        },
      );

    // Subscribe to formControl changes and update datetimeFormControl
    const formToDatetimeSubscription = this.formControl.valueChanges.subscribe(
      (value: number | string | null) => {
        if (!value) {
          this.datetimeFormControl.setValue(null, { emitEvent: false });
        } else {
          const timestamp = !isNaN(+value) ? +value : null;
          if (timestamp !== null) {
            const date = new Date(timestamp / 1000);
            if (!isNaN(date.getTime())) {
              this.datetimeFormControl.setValue(date, { emitEvent: false });
            } else {
              this.datetimeFormControl.setValue(null, { emitEvent: false });
            }
          } else {
            this.datetimeFormControl.setValue(null, { emitEvent: false });
          }
        }
      },
    );

    this.subscription = new Subscription();
    this.subscription.add(datetimeToFormSubscription);
    this.subscription.add(formToDatetimeSubscription);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();

    this.subscription?.unsubscribe();
  }

  clear(): void {
    this.datetimeFormControl.setValue(null);
  }

  private getDefaultDate(
    defaultDateTime: NonNullable<DateTimeProps['defaultDateTime']>,
  ): Date {
    const now = new Date();

    switch (defaultDateTime) {
      case 'now-date-time':
        return now;

      case 'now-date-start-of-day': {
        return new Date(
          // keep Y-M-D
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          // 00:00:00
          0,
          0,
          0,
          0,
        );
      }

      case 'now-date-end-of-day': {
        return new Date(
          // keep Y-M-D
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          // 23:59:59.999
          23,
          59,
          59,
          999,
        );
      }
    }
  }
}
